import React, { useMemo } from "react";
import { Tooltip } from "@material-ui/core";
import EventLabel from "../../../components/EventLabel";

export const getCalculatedOrderRow = (orderRow, breakouts, orders, orderId, mainEvent, modules) => {
  const targetBreakout = orderRow.eventId == mainEvent.eventId ? mainEvent : breakouts.find((b) => b.eventId == orderRow.eventId);

  const getVatExempt = () => {
    const order = orders?.find((a) => a.leader) || orders?.[0];
    if (!order?.["orderExtra"] || !targetBreakout?.allowReverseCharge) return false;
    return order["orderExtra"].find((a) => a.name == "vatExempt")?.value;
  };

  const calculateRowDiscount = () => {
    let discounts = orders.find((o) => o.orderId === orderId)?.discounts || [];
    let discountAmount = 0;

    const amount = orderRow.amount;
    const fullDiscount = discounts.find((x) => x.targetEventId === null && x.valueType === "Percent" && x.value === 100);

    if (fullDiscount != null) {
      return amount;
    }

    if (orderId > 0 && orders.find((o) => o.orderId === orderId)?.orderRow?.find((o) => o.eventId === orderRow.eventId)) {
      discountAmount = orders.find((o) => o.orderId === orderId)?.orderRow?.find((o) => o.eventId === orderRow.eventId)?.discount;
    }

    if (orderId > 0) {
      discounts = orders.find((o) => o.orderId === orderId)?.discounts || [];
    }

    discounts
      .filter((d) => (d.targetEventId === orderRow.eventId && d.value != 0) || (d.targetEventId === null && d.value != 0))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((d) => {
        const decrementValue = d.valueType === "Percent" ? (amount - discountAmount) * (d.value / 100) : d.value;

        if (!d?.allowNegative && (discountAmount + decrementValue < 0 || discountAmount + decrementValue > amount)) {
          discountAmount = amount;
        } else {
          discountAmount += decrementValue;
        }
      });
    return discountAmount;
  };

  const calculateVatAmount = () => {
    const discountAmount = calculateRowDiscount();
    const amount = orderRow.amount;

    if (getVatExempt() || !orderRow.vatPercent) {
      return 0;
    }

    return (amount - discountAmount) * (orderRow.vatPercent / 100);
  };

  const calculateRowSum = () => {
    const discountAmount = calculateRowDiscount();
    const vatAmount = calculateVatAmount();
    const amount = orderRow.amount;
    return amount - discountAmount + vatAmount;
  };

  if (targetBreakout)
    return {
      orderId: orderId,
      eventId: orderRow.eventId,
      orderRowId: orderRow.orderRowId,
      amount: orderRow.amount,
      vatAmount: calculateVatAmount(),
      discountAmount: calculateRowDiscount(),
      rowTotal: calculateRowSum(),
      vatPercent: getVatExempt() ? 0 : orderRow.vatPercent,
      status: orderRow.status?.trim() ?? "Booked",
    };
};

export const ConfirmationOrderRow = ({ orderRow, breakouts, displayConfirmationSettings, environment, mainEvent }) => {
  const { amount, vatAmount, discountAmount, rowTotal, vatPercent, status } = orderRow;

  const targetBreakout = useMemo(() => {
    if (orderRow.hotelAllotmentPricingId) return orderRow;
    if (orderRow.eventId == mainEvent.eventId) return mainEvent;
    return breakouts.find((b) => b.eventId == orderRow.eventId);
  }, [orderRow?.eventId, mainEvent?.eventId, breakouts]);

  const name = useMemo(() => {
    const eds = targetBreakout?.eventDisplayLanguage?.find((eds) => eds.languageCode == environment.lang && eds.alternative == false);
    return eds?.name ?? targetBreakout?.name;
  }, [targetBreakout, environment]);

  const description = useMemo(() => {
    const eds = targetBreakout?.eventDisplayLanguage?.find((eds) => eds.languageCode == environment.lang && eds.alternative == false);
    return eds?.description ?? targetBreakout?.description;
  }, [targetBreakout, environment]);

  if (!targetBreakout && !orderRow?.hotelAllotmentPricingId) return null;
  if (!orderRow?.hotelAllotmentPricingId && environment.eventId == orderRow.eventId && !displayConfirmationSettings["ShowConfMainEvent"]) return null;
  if (!orderRow?.hotelAllotmentPricingId && environment.eventId !== targetBreakout.eventId && !displayConfirmationSettings["ShowConfFreeRows"] && !rowTotal) return null;
  if (!displayConfirmationSettings["ShowConfCancelledRows"] && (status == "Cancelled" || status == "Rejected")) return null;

  return (
    <React.Fragment>
      <tr className="cms-table-row" style={{ whiteSpace: "nowrap" }}>
        <td className="cms-table-cell" style={{ maxWidth: "400px", textOverflow: "ellipsis", overflowX: "hidden" }}>
          <Tooltip title={name}>
            <span>{name}</span>
          </Tooltip>
        </td>
        {displayConfirmationSettings["ShowConfRowStatus"] ? (
          <td className="cms-table-cell">
            <EventLabel name={status} groupName="CMS.OrderSummary" />
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfStartDate"] || displayConfirmationSettings["ShowConfStartTime"] ? (
          <td className="cms-table-cell">
            {displayConfirmationSettings["ShowConfStartDate"] ? new Date(targetBreakout.startDateTime).toLocaleDateString() : null}
            {displayConfirmationSettings["ShowConfStartTime"] && !orderRow?.hotelAllotmentPricingId
              ? (displayConfirmationSettings["ShowConfStartDate"] ? " " : "") + new Date(targetBreakout.startDateTime).toLocaleTimeString([], { timeStyle: "short" })
              : null}
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfEndDate"] || displayConfirmationSettings["ShowConfEndTime"] ? (
          <td className="cms-table-cell">
            {displayConfirmationSettings["ShowConfEndDate"] ? new Date(targetBreakout.endDateTime).toLocaleDateString() : null}
            {displayConfirmationSettings["ShowConfEndTime"] && !orderRow?.hotelAllotmentPricingId
              ? (displayConfirmationSettings["ShowConfEndDate"] ? " " : "") + new Date(targetBreakout.endDateTime).toLocaleTimeString([], { timeStyle: "short" })
              : null}
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfAmount"] ? (
          <td className="cms-table-cell text-right">
            {Math.round(amount, 2)} {` ${environment.currency}`}
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfDiscount"] ? (
          <td className="cms-table-cell text-right">
            {Math.round(discountAmount, 2)} {` ${environment.currency}`}
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfVatAmount"] ? (
          <td className="cms-table-cell text-right">
            {Math.round(vatAmount, 2)} {` ${environment.currency}`}
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfVatPercent"] ? (
          <td className="cms-table-cell text-right">
            {vatPercent}
            {`%`}
          </td>
        ) : null}
        {displayConfirmationSettings["ShowConfQuantity"] ? <td className="cms-table-cell text-right">{1}</td> : null}
        {displayConfirmationSettings["ShowConfRowTotal"] ? (
          <td className="cms-table-cell text-right">
            {Math.round(rowTotal, 2)} {` ${environment.currency}`}
          </td>
        ) : null}
      </tr>
      {displayConfirmationSettings["ShowConfDescription"] && description ? (
        <tr className="cms-table-row" style={{ whiteSpace: "nowrap" }}>
          <td colSpan={100}>{description}</td>
        </tr>
      ) : null}
    </React.Fragment>
  );
};
